import { useEffect, useRef, useState, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { Download, RefreshCw, Search, Trash2, Eye, QrCode, Languages, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const CATS = [{ v: "", l: "Toate" }, { v: "moto", l: "Moto" }, { v: "oem", l: "OEM" }, { v: "parts", l: "Piese" }];
const STATUSES = [
  { v: "new", l: "Nou", c: "bg-blue-600" }, { v: "contacted", l: "Contactat", c: "bg-yellow-600" },
  { v: "interesting", l: "Interesant", c: "bg-green-600" }, { v: "refused", l: "Refuzat", c: "bg-gray-600" },
];
const TAG_FILTERS = [
  { k: "priority", l: "Prioritate", o: ["high", "medium", "low"] },
  { k: "language", l: "Limba", o: ["en", "it", "zh", "hi", "vi"] },
  { k: "eu", l: "Omologare UE", o: ["all", "some", "progress", "no", "dk", "n/a"] },
  { k: "ro", l: "Reprezentant RO", o: ["yes", "no", "dk", "n/a"] },
  { k: "oem", l: "OEM", o: ["yes", "depends", "no", "n/a"] },
  { k: "warranty", l: "Garanție", o: ["m6", "m12", "m24", "m36", "none", "n/a"] },
];
const QR_URLS = ["moto", "oem", "parts"].map(c => ({ c, url: `https://scutersh.store/eicma/${c}` }));
const MAIL = { sent: "bg-green-600", failed: "bg-red-600", pending: "bg-yellow-600" };
const fmt = d => d ? new Date(d).toLocaleString("ro-RO") : "—";

function QrTile({ c, url }) {
  const ref = useRef(null);
  const download = () => {
    const a = document.createElement("a");
    a.href = ref.current.querySelector("canvas").toDataURL("image/png");
    a.download = `QR_eicma_${c}.png`;
    a.click();
  };
  return (
    <div className="flex flex-col items-center gap-2" data-testid={`eicma-qr-${c}`}>
      <div className="bg-white p-3 rounded-lg"><QRCodeCanvas value={url} size={140} level="H" marginSize={2} /></div>
      <div ref={ref} className="hidden"><QRCodeCanvas value={url} size={1024} level="H" marginSize={4} /></div>
      <a href={`/eicma/${c}`} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-primary hover:underline" data-testid={`eicma-qr-link-${c}`}>scutersh.store/eicma/{c}</a>
      <Button size="sm" variant="outline" onClick={download} data-testid={`eicma-qr-download-${c}`}><Download className="w-3 h-3 mr-1" />PNG</Button>
    </div>
  );
}

function LeadDialog({ lead, onClose, onChanged }) {
  if (!lead) return null;
  const c = lead.contact_v2 || {};
  const sendCopy = async () => {
    const force = lead.copy_status === "sent";
    if (force && !window.confirm("Copia a fost deja trimisă. Retrimit?")) return;
    try {
      const r = await api.post(`/admin/eicma/leads/${lead.lead_id}/send-copy`, { force });
      toast[r.data.copy_status === "sent" ? "success" : "error"](r.data.copy_status === "sent" ? "Copie trimisă furnizorului" : "Trimiterea a eșuat");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="eicma-lead-dialog">
        <DialogHeader><DialogTitle>{lead.brand || lead.email} <span className="font-mono text-xs">{lead.lead_id}</span></DialogTitle></DialogHeader>
        <div className="text-sm space-y-1">
          <p><a className="text-primary" href={`mailto:${lead.email}`}>{lead.email}</a>{lead.whatsapp && <> · <a className="text-primary" href={`https://wa.me/${lead.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer">{lead.whatsapp}</a></>}</p>
          <p className="text-muted-foreground">{fmt(lead.created_at)} · {lead.category} · limba {lead.language}{c.booth ? ` · hala ${c.hall || "?"} stand ${c.booth}` : ""}</p>
          {Object.entries(c).filter(([k]) => !["whatsapp", "booth", "hall"].includes(k)).map(([k, v]) => <p key={k}><span className="text-muted-foreground">{k}:</span> {v}</p>)}
          {lead.tags && <div className="flex flex-wrap gap-1 pt-1">{Object.entries(lead.tags).map(([k, v]) => <Badge key={k} variant="outline" data-testid={`eicma-tag-${k}`}>{k}: {v}</Badge>)}</div>}
          {lead.mail_error && <p className="text-red-500 text-xs">Email: {lead.mail_error}</p>}
          {lead.version === 2 && (
            <div className="pt-2 flex items-center gap-2">
              <Button size="sm" onClick={sendCopy} data-testid="eicma-send-copy"><Mail className="w-4 h-4 mr-1" />Trimite copia furnizorului</Button>
              <span className="text-xs text-muted-foreground" data-testid="eicma-copy-status">copie: {lead.copy_status || "not_sent"}</span>
            </div>
          )}
        </div>
        {lead.report && <pre className="mt-3 whitespace-pre-wrap text-xs bg-muted/40 rounded-lg p-3 font-sans" data-testid="eicma-report">{lead.report}</pre>}
        <div className="mt-3 space-y-3">
          {(lead.rows || []).map(([q, a], i) => (
            <div key={`${i}-${q}`} className="border-b border-border pb-2">
              <p className="text-xs text-muted-foreground">{i + 1}. {q}</p>
              <p className="text-sm whitespace-pre-wrap">{a || "—"}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EicmaLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tagFilters, setTagFilters] = useState({ language: "", eu: "", ro: "", oem: "", warranty: "", priority: "" });
  const [notifyEmail, setNotifyEmail] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/eicma/leads", { params: { q, category: cat, status: statusFilter, ...tagFilters } }).then(r => {
      setLeads(r.data);
      setOpen(o => (o ? r.data.find(l => l.lead_id === o.lead_id) || null : null));
    }).catch(() => toast.error("Eroare la încărcare"));
  }, [q, cat, statusFilter, tagFilters]);
  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [load]);
  useEffect(() => { api.get("/admin/eicma/settings").then(r => setNotifyEmail(r.data.notify_email)); }, []);
  const saveNotify = async () => {
    try { await api.put("/admin/eicma/settings", { notify_email: notifyEmail }); toast.success("Email echipă salvat"); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const setStatus = async (id, status) => {
    await api.put(`/admin/eicma/leads/${id}/status`, { status });
    setLeads(ls => ls.map(l => l.lead_id === id ? { ...l, status } : l));
    toast.success("Status actualizat");
  };

  const exportCsv = async () => {
    const r = await api.get("/admin/eicma/leads.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "eicma_leads.csv";
    a.click();
  };
  const retry = async (id) => {
    const r = await api.post(`/admin/eicma/leads/${id}/retry-email`);
    toast[r.data.mail_status === "sent" ? "success" : "error"](r.data.mail_status === "sent" ? "Email trimis" : "Trimiterea a eșuat din nou");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Ștergi definitiv acest răspuns (cerere GDPR)?")) return;
    await api.delete(`/admin/eicma/leads/${id}`);
    toast.success("Șters");
    load();
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="eicma-leads-page">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><QrCode className="w-5 h-5" />Chestionare EICMA – coduri QR</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-8">{QR_URLS.map(x => <QrTile key={x.c} {...x} />)}</CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex flex-wrap items-end gap-2">
            <label className="flex-1 min-w-[220px] text-sm">Email echipă pentru notificări
              <Input value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)} data-testid="eicma-notify-email" className="mt-1" /></label>
            <Button onClick={saveNotify} data-testid="eicma-notify-save">Salvează</Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Răspunsuri furnizori ({leads.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="eicma-search" className="pl-9" placeholder="Caută brand, firmă, email..." value={q} onChange={e => setQ(e.target.value)} />
              </div>
              {CATS.map(c => <Button key={c.v} size="sm" variant={cat === c.v ? "default" : "outline"} onClick={() => setCat(c.v)} data-testid={`eicma-filter-${c.v || "all"}`}>{c.l}</Button>)}
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="eicma-export-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
              <Button size="sm" variant="outline" asChild><Link to="/eicma-traduceri" data-testid="eicma-translations-link"><Languages className="w-4 h-4 mr-1" />Traduceri</Link></Button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="eicma-status-filters">
              <Button size="sm" variant={statusFilter === "" ? "default" : "outline"} onClick={() => setStatusFilter("")} data-testid="eicma-status-filter-all">Toate statusurile</Button>
              {STATUSES.map(s => <Button key={s.v} size="sm" variant={statusFilter === s.v ? "default" : "outline"} onClick={() => setStatusFilter(s.v)} data-testid={`eicma-status-filter-${s.v}`}>{s.l}</Button>)}
            </div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="eicma-tag-filters">
              {TAG_FILTERS.map(f => (
                <select key={f.k} value={tagFilters[f.k]} onChange={e => setTagFilters(s => ({ ...s, [f.k]: e.target.value }))} data-testid={`eicma-filter-tag-${f.k}`}
                  className="bg-background border border-border rounded-md text-xs px-2 py-1.5">
                  <option value="">{f.l}: toate</option>
                  {f.o.map(o => <option key={o} value={o}>{f.l}: {o}</option>)}
                </select>
              ))}
            </div>
            <div className="space-y-2">
              {leads.length === 0 && <p className="text-sm text-muted-foreground" data-testid="eicma-empty">Niciun răspuns încă.</p>}
              {leads.map(l => (
                <div key={l.lead_id} data-testid={`eicma-lead-${l.lead_id}`} className="flex flex-wrap items-center gap-3 border border-border rounded-lg p-3">
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-semibold">{l.brand || l.email} {l.company && <span className="text-muted-foreground font-normal">· {l.company}</span>}
                      {l.tags?.priority && <Badge variant="outline" className="ml-2" data-testid={`eicma-priority-${l.lead_id}`}>{l.tags.priority}</Badge>}</p>
                    <p className="text-xs text-muted-foreground">{fmt(l.created_at)} · {l.category.toUpperCase()} · {l.contact} · {l.email}</p>
                  </div>
                  <select value={l.status} onChange={e => setStatus(l.lead_id, e.target.value)} data-testid={`eicma-status-${l.lead_id}`}
                    className={`${STATUSES.find(s => s.v === l.status)?.c || "bg-gray-600"} text-white text-xs font-semibold rounded-md px-2 py-1.5 border-0`}>
                    {STATUSES.map(s => <option key={s.v} value={s.v} className="bg-background text-foreground">{s.l}</option>)}
                  </select>
                  <Badge className={`${MAIL[l.mail_status] || "bg-gray-600"} text-white`} data-testid={`eicma-mail-${l.lead_id}`}>email: {l.mail_status}</Badge>
                  <Button size="sm" variant="outline" onClick={() => setOpen(l)} data-testid={`eicma-view-${l.lead_id}`}><Eye className="w-4 h-4" /></Button>
                  {l.mail_status !== "sent" && <Button size="sm" variant="outline" onClick={() => retry(l.lead_id)} data-testid={`eicma-retry-${l.lead_id}`}><RefreshCw className="w-4 h-4" /></Button>}
                  <Button size="sm" variant="outline" onClick={() => remove(l.lead_id)} data-testid={`eicma-delete-${l.lead_id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <LeadDialog lead={open} onClose={() => setOpen(null)} onChanged={load} />
    </Layout>
  );
}
